import parse from "html-react-parser";
import { memo, useCallback } from "react";
import { PATH_FAVORITES, PATH_SEARCH_RESULT } from "@routes";
import { ITourElement } from "@interfaces";
import { IconClock } from "@components";
import { sliceString, breakpoint, theme } from "@utilities";
import useImageFallback from "src/Utilities/imageFallbackHelper";
import { Link, useNavigate } from "react-router-dom";
import styled from "styled-components";
import Tooltip from "@mui/material/Tooltip";
import { SearchProductPriceMessage } from "./SearchProductPriceMessage";
import { setSupplier, setName, useAppDispatch } from "@redux";

interface IProps {
    data: ITourElement;
    variant?: "default" | "mobile-card" | "grid-card";
}

export const SearchTourElementModule = memo((props: IProps) => {
    const pathname = window.location.pathname;
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const {
        productImagePath,
        name,
        location,
        durationDays,
        durationNight,
        description,
        supplier: supplierRaw,
        supplierName,
        productSmallToMediumSizeImagePath,
        productLargeSizeImagePath,
        faresprices,
        rrp,
    } = props.data;

    const supplier = supplierRaw || supplierName || null;

    const handleClickSupplier = useCallback(
        (e: React.MouseEvent) => {
            e.preventDefault();
            e.stopPropagation();
            if (supplier) {
                dispatch(setName(""));
                dispatch(setSupplier([{ id: supplier, text: supplier }]));
                navigate(PATH_SEARCH_RESULT);
            }
        },
        [supplier, dispatch, navigate],
    );

    const effectiveFaresPrices: ITourElement["faresprices"] =
        faresprices && faresprices.length > 0
            ? faresprices
            : rrp && Number(rrp) > 0
              ? [{ rrp: Number(rrp) } as any]
              : [];
    // const isLiked = favoritesTours.some((tour) => tour.productId === props.data.productId);
    const isFavoritePage = window.location.pathname === PATH_FAVORITES;

    let duringTime = "";
    if (Number(durationDays) > 0) {
        duringTime += `${durationDays} ${Number(durationDays) === 1 ? "day" : "days"}`;
    }
    if (Number(durationDays) > 0 && Number(durationNight) > 0) {
        duringTime += " , ";
    }
    if (Number(durationNight) > 0) {
        duringTime += `${durationNight} ${Number(durationNight) === 1 ? "night" : "nights"}`;
    }

    const imageSrc = useImageFallback({
        productImagePath,
        productSmallToMediumSizeImagePath,
        productLargeSizeImagePath,
    });

    if (props.variant === "mobile-card" || props.variant === "grid-card") {
        return (
            <MobileCardContainer className={props.variant === "grid-card" ? "grid-card" : ""}>
                <MobileCardLink
                    to={`/detail-tour/${props.data.productId}`}
                    state={{ fromSearchResult: true }}
                    className={props.variant === "grid-card" ? "grid-card-link" : ""}
                >
                    <div
                        className="card-image"
                        style={{ backgroundImage: `url(${imageSrc})` }}
                    />
                    <div className="card-body">
                        <Tooltip title={name || ""} placement="top" arrow slotProps={{ tooltip: { sx: { bgcolor: "#1a1a1a", fontSize: "13px", fontWeight: 600, lineHeight: 1.4, padding: "8px 14px", borderRadius: "8px", maxWidth: 400, textAlign: "center", boxShadow: "0 4px 12px rgba(0,0,0,0.25)" } }, arrow: { sx: { color: "#1a1a1a" } } }}>
                            <p className="card-title">{name}</p>
                        </Tooltip>
                        <p className="card-location">{location}</p>
                        {props.variant === "grid-card" && !!description && typeof description !== "object" && (
                            <p className="card-description">{parse(description)}</p>
                        )}
                        {props.variant === "grid-card" && !!supplier && (
                            <p className="card-supplier">
                                <span className="supplier-link" onClick={handleClickSupplier}>
                                    {sliceString(supplier, 4)}
                                </span>
                            </p>
                        )}
                        <div className="card-footer">
                            {theme.colors.price ? (
                                <SearchPriceBadge>
                                    <span className="badge-price">
                                        {SearchProductPriceMessage(effectiveFaresPrices).price}
                                    </span>
                                </SearchPriceBadge>
                            ) : (
                                <div className="card-price">
                                    <span>From</span>
                                    <span className="price">
                                        {SearchProductPriceMessage(effectiveFaresPrices).price}
                                    </span>
                                </div>
                            )}
                            <div className="card-duration">
                                <IconClock />
                                <span>{duringTime}</span>
                            </div>
                        </div>
                    </div>
                </MobileCardLink>
            </MobileCardContainer>
        );
    }

    return (
        <Container>
            <div
                className={`heart ${pathname === PATH_SEARCH_RESULT ? "searchResultPage" : ""}`}
                style={pathname.includes("favorites") ? { right: "15px" } : {}}
            ></div>
            <StyledTourElementSearch
                to={`/detail-tour/${props.data.productId}`}
                state={{ fromSearchResult: true }}
                style={pathname.includes("favorites") ? { width: "unset", margin: 0 } : {}}
            >
                <div
                    className="tour_image"
                    style={{
                        backgroundImage: `url(${imageSrc})`,
                    }}
                ></div>
                <div className="main">
                    <div className="info">
                        <Tooltip title={name || ""} placement="top" arrow slotProps={{ tooltip: { sx: { bgcolor: "#1a1a1a", fontSize: "13px", fontWeight: 600, lineHeight: 1.4, padding: "8px 14px", borderRadius: "8px", maxWidth: 400, textAlign: "center", boxShadow: "0 4px 12px rgba(0,0,0,0.25)" } }, arrow: { sx: { color: "#1a1a1a" } } }}>
                            <p className={`destination__title ${(name?.length ?? 0) > 50 ? "destination__title--long" : ""} ${(name?.length ?? 0) > 80 ? "destination__title--xlong" : ""}`}>{name}</p>
                        </Tooltip>
                        <p className="destination__place">
                            <span className="placeName">{location}</span>
                        </p>
                        {!isFavoritePage && !!description && !(typeof description === "object") && (
                            <div className="description">
                                <span>{parse(description)}</span>
                            </div>
                        )}
                        {!!supplier && !(typeof description === "object") && (
                            <div className="supplier">
                                <span className="supplier-link" onClick={handleClickSupplier}>
                                    {sliceString(supplier, 4)}
                                </span>
                            </div>
                        )}
                    </div>
                    <div className="durationNPrice">
                        {theme.colors.price ? (
                            <SearchPriceBadge>
                                <span className="badge-price">
                                    {SearchProductPriceMessage(effectiveFaresPrices).price}
                                </span>
                            </SearchPriceBadge>
                        ) : (
                            <div className="destination__price">
                                <span>From</span>
                                <span className="price">
                                    {SearchProductPriceMessage(effectiveFaresPrices).price}
                                </span>
                            </div>
                        )}

                        <div className="destination__duration">
                            <IconClock />
                            <span className="time">{duringTime}</span>
                        </div>
                    </div>
                </div>
            </StyledTourElementSearch>
        </Container>
    );
});
const Container = styled.div`
    position: relative;
`;

const StyledTourElementSearch = styled(Link)`
    display: flex;
    position: relative;
    margin-bottom: 36px;

    ${breakpoint.breakTablet`
        min-height: 208px;
    `}

    ${breakpoint.breakIpadPro`
        min-height: 284px;
    `}

    .tour_image {
        position: relative;
        width: 100%;
        background-repeat: no-repeat;
        background-position: center;
        flex: 1;
        background-color: #fff;
        background-size: cover;
        border-top-left-radius: 8px;
        border-bottom-left-radius: 8px;
    }

    .main {
        flex: 2;
        display: flex;
        flex-direction: column;
        padding-top: 0 !important;

        .info {
            flex: 1;
            background-color: #fff;
            padding: 16px;
            min-height: 110px;
            display: flex;
            flex-direction: column;
            justify-content: space-between;
            border-top-right-radius: 8px;

            ${breakpoint.breakTablet`
                padding: 10px;
                min-height: 80px;
            `}

            ${breakpoint.breakIpadPro`
                padding: 24px;
                min-height: 110px;
            `}
            
            ${breakpoint.breakLarge`
                padding: 31px;
                min-height: 125px;
            `}

            p {
                margin: 0;
                font-weight: 700;
                font-size: 18px;
                line-height: 1.3em;
                color: ${(props) => props.theme.colors.black};
                ${breakpoint.breakTablet`
                    font-size: 12px;
                `}

                ${breakpoint.breakIpadPro`
                    font-size: 16px;
                `}
                
                ${breakpoint.breakLarge`
                    font-size: 20px;
                `}
            }

            .destination__title {
                display: block;
                -webkit-line-clamp: unset;
                -webkit-box-orient: unset;
                overflow: visible;
                text-overflow: unset;
                white-space: normal;
                word-break: break-word;
                min-height: auto;
            }

            .destination__title--long {
                font-size: 15px;
                ${breakpoint.breakTablet`
                    font-size: 11px;
                `}
                ${breakpoint.breakIpadPro`
                    font-size: 14px;
                `}
                ${breakpoint.breakLarge`
                    font-size: 17px;
                `}
            }

            .destination__title--xlong {
                font-size: 13px;
                ${breakpoint.breakTablet`
                    font-size: 10px;
                `}
                ${breakpoint.breakIpadPro`
                    font-size: 12px;
                `}
                ${breakpoint.breakLarge`
                    font-size: 15px;
                `}
            }

            .destination__place {
                margin-top: 12px;
                font-weight: 400;
                display: flex;
                align-items: center;

                ${breakpoint.breakTablet`
                    margin-top: 4px;
                    font-size: 12px;
                `}

                ${breakpoint.breakIpadPro`
                    margin-top: 6px;
                `}
                
                ${breakpoint.breakLarge`
                    margin-top: 12px;
                    font-size: 12px;
                `}
            
                svg {
                    margin-right: 5px;
                    width: 19px;

                    ${breakpoint.breakMobile`
                        margin-right: 10px;
                        width: 15px;
                    `}

                    ${breakpoint.breakTablet`
                        margin-right: 5px;
                    `}

                    ${breakpoint.breakIpadPro`
                        margin-right: 10px;
                        width: 19px;
                    `}
                }
                .placeName {
                    line-height: 1.3em;
                    font-size: 14px;
                    font-weight: 400;

                    ${breakpoint.breakMobile`
                        font-size: 16px;
                    `}

                    ${breakpoint.breakTablet`
                        font-size: 10px;
                    `}

                    ${breakpoint.breakIpadPro`
                        font-size: 12px;
                    `}
                    
                    ${breakpoint.breakLarge`
                        font-size: 16px;
                    `}
                }
            }

            .description {
                margin-top: 12px;
                font-weight: 400;
                font-size: 12px;
                height: 40px;
                overflow: hidden;
                line-height: 22px;
                color: ${({ theme }) => theme.colors.gray_2};

                ${breakpoint.breakTablet`
                    font-size: 10px;
                `}

                ${breakpoint.breakIpadPro`
                    font-size: 12px;
                `}


                ${breakpoint.breakLarge`
                    font-size: 14px;
                `}

                span {
                    text-overflow: ellipsis;
                    -webkit-line-clamp: 2;
                }
            }

            .supplier {
                font-weight: 700;
                font-size: 12px;
                margin-top: 12px;
                line-height: 1.5em;

                ${breakpoint.breakTablet`
                    font-size: 10px;
                `}

                ${breakpoint.breakIpadPro`
                    font-size: 12px;
                `}


                ${breakpoint.breakLarge`
                    font-size: 14px;
                `}

                .supplier-link {
                    color: ${(props) => props.theme.colors.highlight};
                    cursor: pointer;
                    text-decoration: underline;
                    &:hover {
                        opacity: 0.8;
                    }
                }
            }
        }
        .durationNPrice {
            background-color: #fff;
            height: 50px;
            display: flex;
            align-items: center;
            justify-content: space-between;
            color: #000;
            padding: 0 16px;
            border-bottom-right-radius: 8px;
            border-top: 1px solid #e0e0e0;

            ${breakpoint.breakTablet`
                padding: 0 10px;
            `}

            ${breakpoint.breakIpadPro`
                padding: 0 24px;
            `}
            
            ${breakpoint.breakLarge`
                padding: 0 31px;
            `}
        
        .destination__duration {
                display: flex;
                align-items: center;

                svg {
                    margin-right: 5px;
                    width: 19px;

                    ${breakpoint.breakMobile`
                    margin-right: 10px;
                    width: 15px;
                `}

                    ${breakpoint.breakTablet`
                    margin-right: 5px;
                `}

                ${breakpoint.breakIpadPro`
                    margin-right: 10px;
                    width: 19px;
                `}
                
                path {
                        stroke: #000000;
                    }
                }
                span {
                    font-size: 14px;
                    font-weight: 400;
                    white-space: nowrap;

                    ${breakpoint.breakMobile`
                    font-size: 16px;
                `}

                    ${breakpoint.breakTablet`
                    font-size: 10px;
                `}

                ${breakpoint.breakIpadPro`
                    font-size: 12px;
                `}
                
                ${breakpoint.breakLarge`
                    font-size: 16px;
                `}
                }
            }

            .destination__price {
                display: flex;
                align-items: center;

                span {
                    font-size: 12px;
                    font-weight: 400;

                    ${breakpoint.breakMobile`
                    font-size: 14px;
                `}

                    ${breakpoint.breakTablet`
                    font-size: 8px;
                `}

                ${breakpoint.breakIpadPro`
                    font-size: 12px;
                `}
                
                ${breakpoint.breakLarge`
                    font-size: 14px;
                `}
                }

                .price {
                    line-height: 26px;
                    font-size: 16px;
                    font-weight: 700;
                    margin-left: 9px;

                    ${breakpoint.breakMobile`
                    font-size: 20px;
                `}

                    ${breakpoint.breakTablet`
                    font-size: 12px;
                    margin-left: 4px;
                `}

                ${breakpoint.breakIpadPro`
                    font-size: 16px;
                    margin-left: 7px;
                `}
                
                ${breakpoint.breakLarge`
                    font-size: 20px;
                    margin-left: 9px;
                `}
                }
            }
        }
    }
`;

const SearchPriceBadge = styled.div`
    display: inline-flex;
    align-items: center;
    background-color: ${theme.colors.price || theme.colors.highlight};
    color: #fff;
    padding: 4px 12px;
    border-radius: 999px;
    white-space: nowrap;

    .badge-price {
        font-size: 14px;
        font-weight: 700;
    }
`;


const MobileCardContainer = styled.div`
    width: 100%;
    min-width: 0;

    &.grid-card {
        width: 100%;
    }
`;

const MobileCardLink = styled(Link)`
    display: flex;
    flex-direction: column;
    text-decoration: none;
    background: #fff;
    border-radius: 10px;
    overflow: hidden;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    height: 100%;

    .card-image {
        width: 100%;
        height: 140px;
        background-size: cover;
        background-position: center;
        background-repeat: no-repeat;
        background-color: #eee;
    }

    .card-body {
        padding: 10px 12px;
        display: flex;
        flex-direction: column;
        flex: 1;
    }

    .card-title {
        font-size: 14px;
        font-weight: 700;
        color: ${({ theme }) => theme.colors.black};
        margin: 0 0 4px;
        display: -webkit-box;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
        line-height: 1.3;
    }

    .card-location {
        font-size: 12px;
        color: #666;
        margin: 0 0 8px;
        font-weight: 400;
    }

    .card-footer {
        margin-top: auto;
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 6px;
    }

    .card-price {
        display: flex;
        align-items: baseline;
        gap: 4px;

        span {
            font-size: 11px;
            color: #888;
        }

        .price {
            font-size: 15px;
            font-weight: 700;
            color: #000;
        }
    }

    .card-duration {
        display: flex;
        align-items: center;
        gap: 3px;

        svg {
            width: 14px;
            path {
                stroke: #888;
            }
        }

        span {
            font-size: 11px;
            color: #888;
            white-space: nowrap;
        }
    }

    &.grid-card-link {
        .card-image {
            height: 200px;
        }

        .card-body {
            padding: 14px 16px;
        }

        .card-title {
            font-size: 16px;
            -webkit-line-clamp: 2;
            min-height: 42px;
        }

        .card-location {
            font-size: 13px;
            margin-bottom: 6px;
        }

        .card-description {
            font-size: 12px;
            color: #666;
            margin: 0 0 6px;
            font-weight: 400;
            display: -webkit-box;
            -webkit-line-clamp: 2;
            -webkit-box-orient: vertical;
            overflow: hidden;
            line-height: 1.5;
        }

        .card-supplier {
            font-size: 12px;
            font-weight: 700;
            margin: 0 0 10px;
            color: ${({ theme }) => theme.colors.black};

            .supplier-link {
                /* Same one-off grey as the breadcrumb and location picker. */
                color: #747474;
                cursor: pointer;
                text-decoration: underline;
                &:hover {
                    opacity: 0.8;
                }
            }
        }

        .card-price .price {
            font-size: 18px;
        }

        .card-duration span {
            font-size: 12px;
        }

        @media (max-width: 768px) {
            .card-image {
                height: 160px;
            }

            .card-body {
                padding: 10px 12px;
            }

            .card-title {
                font-size: 14px;
                min-height: unset;
                -webkit-line-clamp: 2;
                margin: 0 0 3px;
            }

            .card-location {
                font-size: 11px;
                margin-bottom: 4px;
            }

            .card-description {
                -webkit-line-clamp: 2;
                font-size: 11px;
                margin: 0 0 4px;
            }

            .card-supplier {
                display: none;
            }

            .card-price .price {
                font-size: 15px;
            }

            .card-duration span {
                font-size: 11px;
            }
        }
    }
`;
